/**
 * ClaraGraphEmbed — SPIKE SCAFFOLD (not yet wired into ClaraChat).
 *
 * Goal: render ONE real Data Review modality graph inside Clara, instead of the
 * custom SVG (ClaraTrendChart), so the chart the clinician sees is the exact
 * component they already trust — same renderer, same streaming data path.
 *
 * What the spike verified against the real code:
 *  - <ModalityGraphGroup json={...} area={...} /> drives a graph from its `json`
 *    prop when the config atom is empty (ModalityGraphGroup.tsx:80-110).
 *  - It already wraps itself in DimensionsProvider; the ONE wrapper we must add is
 *    EventReviewProvider (it calls useEventReviewProvider()).
 *  - currentPatientFileInfoAtom must be populated (fileStartDate/End, dataSourceMap,
 *    patientModalities) — ~3 cheap API calls, no heavy prefetch.
 *  - The socket + page pipeline are global/inherited; nothing to stand up.
 *
 * The three bits that still need BROWSER-IN-THE-LOOP iteration (left as TODOs):
 *  1. VIEW POSITIONING — viewScaleRegistry opens at fileStart; we must push the
 *     queried window [start,end] (last N h ending at referenceEndMs). Note the
 *     timezone subtlety: file dates are local-to-patient, not UTC.
 *  2. HIDE THE WINDOW TOOLBAR — ModalityGraphGroup renders <WindowSettings>; a
 *     chat embed wants it hidden (CSS or a prop — TBD).
 *  3. EPISODE ANNOTATIONS — inject Clara's episodes into annotationsAtom (need the
 *     exact Annotation constructor signature — verify before using).
 */
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useSetRecoilState } from "recoil"
import { scaleUtc } from "d3"

import { useEndpointProvider } from "../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../Providers/BackendLinksProvider"
import { EventReviewProvider } from "../../Providers/EventReviewProvider"

import { ModalityGraphGroup } from "../Data/Visualize/DataReview/Components/Visualizations/TimeSeriesGraphGroup/React/ModalityGraphGroup"
import { currentPatientFileInfoAtom } from "../Data/Visualize/DataReview/Atoms/PatientFile"
import { DataSource } from "../Data/Visualize/DataReview/Types/DataSource"
import { RenderStrategy } from "../Data/Visualize/DataReview/Types/Trace"
import { VisualizationComponent } from "../Data/Visualize/DataReview/VisualizationComponent"
import { viewScaleRegistry } from "../Data/Visualize/DataReview/Data/ViewScaleRegistry"
import { useRecoilValue } from "recoil"
import { selectedLayoutIdAtom } from "../Data/Visualize/DataReview/Atoms/Layout"
import { annotationsAtom } from "../Data/Visualize/DataReview/Atoms/Annotations"
import { Annotation } from "../../Managers/VisualizationManager/Variables/Annotations"
import styles from "./Clara.module.css"

// Tall enough to read the trace across the width of the conversation (UTSW NSICU, 24 Sep:
// the graph was "very hard to read on a screen").
const EMBED_HEIGHT = 420
// Several signals stack one graph each, and three or four grow the chart at about this much a graph.
const GRAPH_HEIGHT = 140

// Map a Clara modality key -> a sensible default y-range + label for the graph.
// (Data Review normally autoscales; these are just initial bounds.)
const MODALITY_META = {
	HR_na: { name: "Heart rate", units: "bpm", min: 40, max: 160 },
	SpO2_na: { name: "SpO2", units: "%", min: 70, max: 100 },
	ICP_Mean: { name: "ICP", units: "mmHg", min: 0, max: 40 },
	ABP_Mean: { name: "Mean arterial pressure", units: "mmHg", min: 40, max: 140 },
	EtCO2_na: { name: "EtCO2", units: "mmHg", min: 20, max: 60 },
}

// One color per signal, the same in every answer, that reads on the light and dark chart backgrounds.
// ICP, MAP, heart rate, SpO2 and EtCO2 stay apart from each other for color blind readers, except SpO2
// with EtCO2. Each graph also names its signal.
const SIGNAL_COLORS = {
	ICP_Mean: "#bb7c00",
	EtCO2_na: "#256abf",
	ABP_Mean: "#199e70",
	HR_na: "#b5338a",
	SpO2_na: "#8a7ce6",
	RR_na: "#008300",
	ABP_Syst: "#d95926",
	ABP_Dias: "#3987e5",
}
const NO_EPISODES = []

export function ClaraGraphEmbed({ patientId, dataObjectId, referenceEndMs, modality, archiveLabel, series, thresholds = [], valueMin, valueMax, windowHours = 4, episodes = NO_EPISODES, fallback = null }) {
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const setPatientFileInfo = useSetRecoilState(currentPatientFileInfoAtom)
	const setAnnotations = useSetRecoilState(annotationsAtom)
	// The component keys its config/scale registries by { layoutId: selectedLayoutId,
	// windowId: area }. selectedLayoutId is null on the Clara page; read it so our
	// view-positioning set() uses the SAME key the component reads with.
	const selectedLayoutId = useRecoilValue(selectedLayoutIdAtom)
	// Gate the graph on the patient-file load: if it mounts before fileStart/End are
	// known, the file-scale registry caches the default (epoch 0 ≈ 1970), which is
	// why the bottom timeline read "1980 2000 2020".
	const [loaded, setLoaded] = useState(false)
	// The metadata fetch can fail, and it can also simply never settle (a dropped
	// request has no error). Either way the reader must not be left with a blank
	// rectangle: failed => render the caller's fallback chart instead.
	const [failed, setFailed] = useState(false)
	const frameRef = useRef(null)
	const [active, setActive] = useState(false)

	const meta = MODALITY_META[modality] || { name: modality, units: "", min: 0, max: 100 }
	// The channel to CHART. The archive's own label when the server reported one
	// (ART_Mean at UTSW), the canonical name otherwise. The two differ exactly at
	// sites whose monitors write a different label family, and charting the
	// canonical name there asks the page pipeline for a channel the archive does
	// not have: axes render, the plot stays empty, which is precisely what
	// "Show me MAP" did at UTSW while the SVG beside it had the data. `modality`
	// stays the DISPLAY identity (name, units, default bounds).
	const chartKey = archiveLabel || modality
	// Show the unit in the graph label, e.g. "Heart rate (bpm)".
	const displayName = meta.units ? `${meta.name} (${meta.units})` : meta.name
	// An answer about several signals (its view's series) charts each one in its own graph.
	const chartKeys = series ? series.map(item => item.archive_label || item.signal) : [chartKey]
	const height = series ? Math.max(EMBED_HEIGHT, series.length * GRAPH_HEIGHT + 80) : EMBED_HEIGHT

	// One graph slot PER QUERY (modality + window + reference end). A fixed area made
	// the config/scale atoms from a previous question stick (e.g. an SpO2 question
	// reusing the earlier HR graph). Unique area => fresh config + scales each time.
	// The area becomes part of a CSS class/selector (d3 clip-path), so it MUST be a
	// valid CSS identifier — strip anything outside [a-zA-Z0-9_-] (e.g. the decimal
	// point in a float referenceEndMs, which makes querySelectorAll throw).
	const cssSafe = value => String(value).replace(/[^a-zA-Z0-9_-]/g, "")
	// Include the thresholds in the key: a summary (no lines) and an "over 120" query for
	// the SAME modality/window/reference-end must NOT share a cached config, or the
	// summary inherits the earlier query's threshold line.
	const thresholdKey = thresholds.length ? thresholds.map(t => cssSafe(t)).join("_") : "none"
	const area = `clara-embed-${cssSafe(series ? series.map(item => item.signal).join("_") : modality)}-${cssSafe(Math.round(Number(referenceEndMs)))}-${cssSafe(windowHours)}-${thresholdKey}`

	// (1) Populate currentPatientFileInfoAtom for this patient/data-object.
	useEffect(() => {
		let cancelled = false
		async function load() {
			// Two confirmed endpoints (cf. Visualization.js): data-object metadata
			// (start/end/timezone) and admission info (is_admitted). We don't need the
			// modalities-union call — we only chart the one modality we configure.
			try {
				const [metadata, admission] = await Promise.all([
					endpointProvider.post(LINKS.VISUALIZE.GET_DATA_OBJECT_BY_ID, { data_object_id: Number(dataObjectId) }),
					endpointProvider.post(LINKS.VISUALIZE.GET_ADMISSION_INFO, { patient_id: Number(patientId) }),
				])
				if (cancelled) return
				setPatientFileInfo({
					fileStartDate: new Date(metadata.start_time),
					fileEndDate: new Date(metadata.end_time),
					// A number, as Data Review sends it. The query service checks this id against
					// the recording's owner with ==, and "17" is not 17, so a string failed every
					// page until Data Review had opened that recording once.
					patientId: Number(patientId),
					patientIdentifier: String(patientId),
					dataSourceMap: new Map([[DataSource.CURRENT_PATIENT, Number(dataObjectId)]]),
					patientModalities: chartKeys,
					timeZone: metadata.time_zone || "America/New_York",
					montageMapping: new Map(),
					isAdmitted: Boolean(admission?.is_admitted),
					persystRecordingInfo: null,
					persystMontageDefinitions: [],
					gatewayId: undefined,
					mrnError: undefined,
				})
				setLoaded(true)
			} catch (error) {
				if (!cancelled) {
					setFailed(true)
				}
			}
		}
		load()
		return () => {
			cancelled = true
		}
	}, [patientId, dataObjectId, chartKeys.join(","), endpointProvider, LINKS, setPatientFileInfo])

	// A watchdog for the no-error stall: if the metadata has not arrived after 12
	// seconds, stop waiting and hand back to the fallback. The shimmer promises the
	// reader something is coming; a promise that can be kept forever is a lie.
	useEffect(() => {
		if (loaded || failed) return undefined
		const timer = setTimeout(() => setFailed(true), 12000)
		return () => clearTimeout(timer)
	}, [loaded, failed])

	// (1b) View positioning: push the queried window into the registry BEFORE the
	// graph reads it. NOTE: this is the part most likely to need live tuning —
	// timezone alignment (file-local vs UTC) and whether the controller re-reads
	// the registry after mount.
	const viewDuration = windowHours * 3_600_000
	useMemo(() => {
		if (!referenceEndMs) return
		const end = Number(referenceEndMs)
		const start = end - viewDuration
		// Key MUST match what the component reads with: { layoutId: selectedLayoutId,
		// windowId: area }. (Previously used layoutId: undefined, so the set was a
		// no-op and the view opened at the file start with no data.)
		// NOTE: still a candidate for the timezone wrinkle — file dates are patient-
		// local, referenceEndMs is true epoch. If the window lands off, that's why.
		viewScaleRegistry.set(
			{ layoutId: selectedLayoutId, windowId: area },
			VisualizationComponent.TIME_SERIES_GROUP,
			scaleUtc().domain([new Date(start), new Date(end)])
		)
	}, [referenceEndMs, viewDuration, selectedLayoutId, area])

	// (2) Minimal single-trace config passed via the `json` prop.
	const json = useMemo(
		() => {
			// When there are reference lines, fix the y-range to span the DATA *and* both
			// bounds — otherwise Data Review autoscales to the signal alone and a far
			// threshold line (e.g. 110 over a flat 91) renders off-screen. Falls back to
			// autoscale when there's no threshold.
			const yValues = [
				...(Number.isFinite(valueMin) ? [Number(valueMin)] : []),
				...(Number.isFinite(valueMax) ? [Number(valueMax)] : []),
				...thresholds.map(Number),
			]
			const fixRange = thresholds.length > 0 && yValues.length > 0
			let yMin = meta.min
			let yMax = meta.max
			let autoScale = true
			if (fixRange) {
				const lo = Math.min(...yValues)
				const hi = Math.max(...yValues)
				const pad = Math.max(2, (hi - lo) * 0.15)
				yMin = lo - pad
				yMax = hi + pad
				autoScale = false
			}
			if (series) {
				return {
					viewDuration,
					playbackSpeed: 1,
					graphs: series.map((item, index) => {
						const key = chartKeys[index]
						const bounds = MODALITY_META[item.signal] || { min: 0, max: 100 }
						const name = item.unit ? `${item.display} (${item.unit})` : item.display
						const color = SIGNAL_COLORS[item.signal] || "#5a86ff"
						return {
							id: `clara-graph-${index}`,
							name,
							min: bounds.min,
							max: bounds.max,
							defaultMin: bounds.min,
							defaultMax: bounds.max,
							autoScale: true,
							heightRatio: 1,
							renderStrategy: RenderStrategy.LINE,
							summaryValueConfigs: [],
							summaryValueColumns: 1,
							traces: [
								{
									id: `clara-trace-${index}`,
									dataKey: key,
									rawDataKey: key,
									dataSource: DataSource.CURRENT_PATIENT,
									name,
									units: item.unit || "",
									renderStrategy: RenderStrategy.LINE,
									color,
									isVisible: true,
									isCompositePart: false,
								},
							],
						}
					}),
				}
			}
			return {
			viewDuration,
			playbackSpeed: 1,
			graphs: [
				{
					id: "clara-graph",
					name: displayName,
					min: yMin,
					max: yMax,
					defaultMin: yMin,
					defaultMax: yMax,
					autoScale,
					heightRatio: 1,
					renderStrategy: RenderStrategy.LINE,
					summaryValueConfigs: [],
					summaryValueColumns: 1,
					// Dashed reference line(s) at the queried threshold(s) — two for a range
					// query (new D3ThresholdLines primitive).
					thresholds: thresholds.length ? thresholds.map(v => ({ value: Number(v), color: "#e2725b", isDashed: true })) : undefined,
					traces: [
						{
							id: "clara-trace",
							dataKey: chartKey,
							rawDataKey: chartKey,
							dataSource: DataSource.CURRENT_PATIENT,
							name: displayName,
							units: meta.units,
							renderStrategy: RenderStrategy.LINE,
							color: "#5a86ff",
							isVisible: true,
							isCompositePart: false,
						},
					],
				},
			],
		}
		},
		[viewDuration, chartKey, meta, thresholds.join(","), valueMin, valueMax, series, chartKeys.join(",")]
	)

	// Episode shading via the existing annotations system (no shared-code change —
	// annotations shade any time region). Set on mount, clear on unmount so one
	// question's episodes don't bleed into the next. (annotationsAtom is global.)
	useEffect(() => {
		const annotations = (episodes || []).map(
			(ep, i) => new Annotation(`clara-ep-${area}-${i}`, null, null, "episode", "", "", ep.start, ep.end, [], "#0c8ce9", 0.22, [])
		)
		setAnnotations(annotations)
		return () => setAnnotations([])
	}, [episodes, area, setAnnotations])

	useEffect(() => {
		const frame = frameRef.current
		if (!frame) return undefined
		const onWheel = event => (active ? event.preventDefault() : event.stopPropagation())
		frame.addEventListener("wheel", onWheel, { capture: true, passive: false })
		return () => frame.removeEventListener("wheel", onWheel, { capture: true })
	}, [active, failed])

	useEffect(() => {
		if (!active) return undefined
		const onDown = event => !frameRef.current?.contains(event.target) && setActive(false)
		const onKey = event => event.key === "Escape" && setActive(false)
		const onLeave = () => setActive(false)
		const frame = frameRef.current
		document.addEventListener("mousedown", onDown, true)
		document.addEventListener("keydown", onKey)
		frame?.addEventListener("mouseleave", onLeave)
		return () => {
			document.removeEventListener("mousedown", onDown, true)
			document.removeEventListener("keydown", onKey)
			frame?.removeEventListener("mouseleave", onLeave)
		}
	}, [active])

	if (failed) {
		// The deterministic SVG chart from the answer itself, when the caller passed
		// one. Never a blank box, and never a shimmer with nothing behind it.
		return fallback
	}

	return (
		// Explicit height — the graph collapses to 0px without it.
		// Outer crops away the (now CSS-hidden) timeline's reserved space; the inner
		// div is the height the graph actually measures. Tune the outer height if a
		// gap/crop shows once the timeline is hidden.
		<div
			ref={frameRef}
			className={`${styles.embedCrop} ${active ? styles.embedActive : ""}`}
			style={{ height, width: "100%", overflow: "hidden" }}
			onMouseDownCapture={() => setActive(true)}
		>
			<div className={styles.embedHint}>{active ? "Scroll moves the timeline. Click outside or press Esc to stop" : "Click the chart to scroll its timeline"}</div>
			<div style={{ height: height + 32, width: "100%" }}>
				{loaded ? (
					<EventReviewProvider>
						<ModalityGraphGroup json={json} area={area} hideToolbar />
					</EventReviewProvider>
				) : (
					<div className={styles.embedShimmer} aria-label="Loading chart" />
				)}
			</div>
		</div>
	)
}
